import { useEffect, useState } from 'react';
import { SoftBadge } from '@ab-platform/ui/soft-badge';
import {
  getIdentityVerificationStatus,
  type KycNormalizedStatus,
} from '@/lib/api';
import {
  compactIdentityLabel,
  deriveIdentityVerificationView,
} from '@/lib/identity-verification-view';

/**
 * Compact, READ-ONLY identity status line for the documents page (PR-DIDIT-6B).
 * One-shot status read, no polling, no CTA — the actions live on the dedicated
 * `/verifica-identita` step. Renders nothing when KYC is not required or the
 * read fails (the documents flow handles token expiry independently).
 */
export function IdentityVerificationSummary({
  uploadToken,
}: {
  uploadToken: string;
}): JSX.Element | null {
  const [status, setStatus] = useState<KycNormalizedStatus | null>(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let active = true;
    void getIdentityVerificationStatus(uploadToken).then((res) => {
      if (!active) return;
      if (res.ok && res.data.requiresKyc) {
        setStatus(res.data.status);
      } else {
        // Not required, or any error (incl. expired token) → stay silent.
        setHidden(true);
      }
    });
    return () => {
      active = false;
    };
  }, [uploadToken]);

  if (hidden || status === null) return null;

  const tone = deriveIdentityVerificationView(status).tone;
  return (
    <div className="flex items-center gap-2 rounded-md bg-foreground/5 px-3 py-2 text-xs">
      <span className="text-muted-foreground">Identità</span>
      <SoftBadge tone={tone}>{compactIdentityLabel(status)}</SoftBadge>
    </div>
  );
}
